import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, mergeProps as _mergeProps, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "d-flex flex-lg-row flex-wrap align-center ga-2 ga-lg-6 justify-space-between" }
const _hoisted_2 = ["textContent"]
const _hoisted_3 = { class: "order-last order-lg-1 w-100 w-lg-auto d-flex ga-2 flex-row filters" }
const _hoisted_4 = {
  key: 0,
  class: "pl-2"
}
const _hoisted_5 = { class: "order-2 ml-auto pl-2" }
const _hoisted_6 = { class: "text-center" }

import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { refThrottled } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import { Encoded } from "@aeternity/aepp-sdk";

import type { ObjectValues } from "@/utils/types";
import { ROUTE_CREATE_TOKEN } from "@/utils/constants";
import { includes } from "@/utils";
import { TokensService } from "@/api/generated";
import WebSocketClient from "@/libs/WebSocketClient";
import { useCommunityFactory, useNetwork } from "@/composables";
import { useAccounts } from "@/stores";

import WalletConnectCard from "@/components/WalletConnect/WalletConnectCard.vue";
import TokenListTable from "@/components/Tokens/TokenListTable.vue";

type SelectOptions<T> = {
  title: string;
  disabled?: boolean;
  value: T;
}[];

type OrderByOption = ObjectValues<typeof SORT>;
type CollectionOption = "all" | `${string}-${Encoded.AccountAddress}`;


export default /*@__PURE__*/_defineComponent({
  __name: 'HomeView',
  setup(__props) {

const SORT = {
  marketCap: "market_cap",
  newest: "newest",
  oldest: "oldest",
} as const;
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { smAndUp } = useDisplay();
const { activeNetworkId } = useNetwork();
const { activeAccount } = storeToRefs(useAccounts());
const { activeFactorySchema, activeFactoryCollectionsNum } =
  useCommunityFactory();

const collection = ref<CollectionOption>(
  (route.query.collection as any) ?? "all"
);
const orderBy = ref<OrderByOption>((route.query.sort as any) ?? SORT.marketCap);
const ownedOnly = ref(false);
const search = ref<string>(route.query.search?.toString() ?? "");
const searchThrottled = refThrottled(search, 2000);

function updateCollection(val: CollectionOption) {
  collection.value = val;
  router.push({
    query: { ...route.query, collection: val },
  });
}

function updateOrderBy(val: OrderByOption) {
  orderBy.value = val;
  router.push({
    query: { ...route.query, sort: val },
  });
}

const orderByOptions: SelectOptions<OrderByOption> = [
  // {
  //   title: t("common.name"),
  //   value: "name",
  // },
  {
    title: t("common.marketCap"),
    value: "market_cap",
  },
  {
    title: t("common.newest"),
    value: "newest",
  },
  {
    title: t("common.oldest"),
    value: "oldest",
  },
  // {
  //   title: t("common.rank"),
  //   value: "rank",
  // },
  // {
  //   title: t("common.creationDate"),
  //   value: "created_at",
  // },
];

const collectionOptions = computed<SelectOptions<CollectionOption>>(() => {
  const collections = activeFactorySchema.value?.collections || {};
  return [
    {
      title: t("common.allTokens"),
      value: "all",
    },
    ...Object.values(collections).map((collection) => ({
      title: t(`collections.${collection.name}.only`),
      value: collection.id as CollectionOption,
    })),
  ];
});

const activeCollectionOption = computed(() =>
  collectionOptions.value.find((option) => option.value === collection.value)
);
const activeSortOption = computed(() =>
  orderByOptions.find((option) => option.value === orderBy.value)
);

const scrollComponent = ref<HTMLElement>();
const newTokenListenerSubscription = ref();
let refetchTimeout: NodeJS.Timeout;

const orderByMapped = computed(() =>
  includes([SORT.newest, SORT.oldest], orderBy.value)
    ? "created_at"
    : orderBy.value
);

const orderDirection = computed((): "ASC" | "DESC" =>
  orderBy.value === "oldest" ? "ASC" : "DESC"
);

const { data, isFetching, fetchNextPage, hasNextPage, refetch } =
  useInfiniteQuery({
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) =>
      TokensService.listAll({
        orderBy: orderByMapped.value as any,
        orderDirection: orderDirection.value,
        collection: collection.value,
        search: searchThrottled.value,
        ownerAddress: ownedOnly.value
          ? (activeAccount.value as string)
          : undefined,
        limit: 20,
        page: pageParam,
      }),
    getNextPageParam: (lastPage: any, allPages, lastPageParam) => {
      if (lastPage?.meta?.currentPage === lastPage?.meta?.totalPages) {
        return undefined;
      }

      return lastPageParam + 1;
    },
    queryKey: [
      "TokensService.listAll",
      orderBy,
      collection,
      orderByMapped,
      searchThrottled,
      activeNetworkId,
      ownedOnly,
      activeAccount,
    ],
  });

const handleScroll = () => {
  if (isFetching.value) {
    return;
  }
  clearTimeout(refetchTimeout);
  refetchTimeout = setTimeout(() => {
    if (
      (hasNextPage.value &&
        scrollComponent.value?.getBoundingClientRect()?.bottom) ||
      0 < window.innerHeight
    ) {
      fetchNextPage();
    }
  }, 500);
};

watch(searchThrottled, (val, oldVal) => {
  if (val !== oldVal) {
    router.push({
      query: { ...route.query, search: val },
    });
  }
});

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
  newTokenListenerSubscription.value = WebSocketClient.subscribeToNewTokenSales(
    (token: any) => {
      console.log("New token sale::", token);
      clearTimeout(refetchTimeout);
      refetchTimeout = setTimeout(() => {
        refetch();
      }, 4000);
    }
  );
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
  newTokenListenerSubscription.value?.();
});

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!

  return (_openBlock(), _createElementBlock("div", {
    class: "home-view container",
    ref_key: "scrollComponent",
    ref: scrollComponent
  }, [
    _createVNode(WalletConnectCard),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_component_v_btn, {
        block: "",
        variant: "outlined",
        class: "order-first mb-4 d-md-none",
        to: { name: _unref(ROUTE_CREATE_TOKEN) }
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t("blocks.appHeader.createTokenMobile")), 1)
        ]),
        _: 1
      }, 8, ["to"]),
      _createElementVNode("h2", {
        textContent: _toDisplayString(_ctx.$t('common.tokenList')),
        class: "order-first text-h4 mr-md-12"
      }, null, 8, _hoisted_2),
      _createElementVNode("div", _hoisted_3, [
        (_unref(activeFactoryCollectionsNum) > 1)
          ? (_openBlock(), _createBlock(_component_v_menu, { key: 0 }, {
              activator: _withCtx(({ props }) => [
                _createVNode(_component_v_btn, _mergeProps({
                  variant: "outlined",
                  size: _unref(smAndUp) ? 'large' : undefined,
                  class: "px-1 px-sm-4 flex-grow-1"
                }, props), {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(activeCollectionOption.value?.title) + " ", 1),
                    _createVNode(_component_v_icon, null, {
                      default: _withCtx(() => _cache[2] || (_cache[2] = [
                        _createTextVNode("mdi-chevron-down")
                      ])),
                      _: 1
                    })
                  ]),
                  _: 2
                }, 1040, ["size"])
              ]),
              default: _withCtx(() => [
                _createVNode(_component_v_list, null, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(collectionOptions.value, ({ disabled, title, value }, index) => {
                      return (_openBlock(), _createBlock(_component_v_list_item, {
                        key: index,
                        disabled: disabled,
                        onClick: ($event: any) => (updateCollection(value))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_list_item_title, {
                            class: _normalizeClass({
                  'text-primary': collection.value == value,
                })
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(title) + " ", 1),
                              disabled
                                ? (_openBlock(), _createElementBlock("small", _hoisted_4, " (" + _toDisplayString(_ctx.$t("common.comingSoon")) + ") ", 1))
                                : _createCommentVNode("", true)
                            ]),
                            _: 2
                          }, 1032, ["class"])
                        ]),
                        _: 2
                      }, 1032, ["disabled", "onClick"]))
                    }), 128))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (_unref(activeAccount))
          ? (_openBlock(), _createBlock(_component_v_btn, {
              key: 1,
              variant: "outlined",
              size: _unref(smAndUp) ? 'large' : undefined,
              class: "px-1 px-sm-4 flex-grow-1",
              style: {"min-width":"auto"},
              color: ownedOnly.value ? 'primary' : undefined,
              onClick: _cache[0] || (_cache[0] = ($event: any) => (ownedOnly.value = !ownedOnly.value))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(smAndUp)
              ? _ctx.$t("views.home.showOwnedOnly")
              : _ctx.$t("views.home.showOwnedOnlyShort")), 1)
              ]),
              _: 1
            }, 8, ["size", "color"]))
          : _createCommentVNode("", true),
        _createVNode(_component_v_menu, null, {
          activator: _withCtx(({ props }) => [
            _createVNode(_component_v_btn, _mergeProps({
              variant: "outlined",
              size: _unref(smAndUp) ? 'large' : undefined,
              class: "px-1 px-sm-4 flex-grow-1"
            }, props), {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(activeSortOption.value?.title) + " ", 1),
                _createVNode(_component_v_icon, null, {
                  default: _withCtx(() => _cache[3] || (_cache[3] = [
                    _createTextVNode("mdi-chevron-down")
                  ])),
                  _: 1
                })
              ]),
              _: 2
            }, 1040, ["size"])
          ]),
          default: _withCtx(() => [
            _createVNode(_component_v_list, null, {
              default: _withCtx(() => [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(orderByOptions, ({ title, value }, index) => {
                  return _createVNode(_component_v_list_item, {
                    key: index,
                    onClick: ($event: any) => (updateOrderBy(value))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_list_item_title, {
                        class: _normalizeClass({
                  'text-primary': orderBy.value === value,
                })
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(title), 1)
                        ]),
                        _: 2
                      }, 1032, ["class"])
                    ]),
                    _: 2
                  }, 1032, ["onClick"])
                }), 64))
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(_component_v_text_field, {
          modelValue: search.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((search).value = $event)),
          placeholder: _ctx.$t('views.home.searchLabel'),
          variant: "outlined",
          "append-inner-icon": "mdi-magnify",
          density: "compact",
          class: "search-field d-block h-100",
          "hide-details": ""
        }, null, 8, ["modelValue", "placeholder"])
      ])
    ]),
    _createVNode(_component_v_row, { class: "position-relative mt-sm-1 mt-md-4" }, {
      default: _withCtx(() => [
        (
          (!_unref(data)?.pages?.length || !_unref(data)?.pages[0].items.length) && !_unref(isFetching)
        )
          ? (_openBlock(), _createBlock(_component_v_alert, {
              key: 0,
              textContent: _toDisplayString(_ctx.$t('views.home.noTokenSales')),
              type: "info",
              outlined: "",
              class: "ma-3"
            }, null, 8, ["textContent"]))
          : _createCommentVNode("", true),
        _createVNode(_component_v_col, { cols: "12" }, {
          default: _withCtx(() => [
            _createVNode(TokenListTable, {
              pages: _unref(data)?.pages,
              loading: _unref(isFetching)
            }, null, 8, ["pages", "loading"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_6, [
      (_unref(hasNextPage))
        ? (_openBlock(), _createBlock(_component_v_btn, {
            key: 0,
            textContent: _toDisplayString(_ctx.$t('common.loadMore')),
            variant: "outlined",
            rounded: "",
            size: "large",
            loading: _unref(isFetching),
            onClick: _unref(fetchNextPage)
          }, null, 8, ["textContent", "loading", "onClick"]))
        : _createCommentVNode("", true)
    ])
  ], 512))
}
}

})